import { ExternalLink, ShieldCheck, HandCoins, FileText, Lock, MapPinned } from "lucide-react";
import { Link } from "react-router-dom";
import { tr } from "../../lib/i18n";

const money = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// Read-only resource blocks shared by the Bereavement Pack and the public share view.
export const CostTable = ({ costs, addOns, note, sources = [] }) => (
  <div data-testid="funeral-costs">
    <div className="rounded-xl border border-border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-muted/60 text-[10px] uppercase tracking-widest font-mono text-muted-foreground"><tr><th className="text-left px-3 py-2">{tr("Option")}</th><th className="text-right px-3 py-2">{tr("Typical")}</th><th className="text-right px-3 py-2 hidden sm:table-cell">{tr("Range")}</th></tr></thead>
        <tbody className="divide-y divide-border">
          {costs.map((c) => (
            <tr key={c.id} data-testid={`funeral-cost-${c.id}`}>
              <td className="px-3 py-2.5"><div className="font-medium">{c.label}</div><div className="text-xs text-muted-foreground">{c.note}</div></td>
              <td className="px-3 py-2.5 text-right font-mono whitespace-nowrap">{money(c.typical)}</td>
              <td className="px-3 py-2.5 text-right font-mono text-xs text-muted-foreground whitespace-nowrap hidden sm:table-cell">{money(c.low)} – {money(c.high)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <div className="mt-3 flex flex-wrap gap-1.5">{addOns.map((a) => <span key={a.label} className="text-[11px] rounded-full bg-muted px-2.5 py-1"><span className="text-muted-foreground">{a.label}</span> <span className="font-mono">{a.range}</span></span>)}</div>
    {note && <p className="mt-2 text-[11px] text-muted-foreground">{note}</p>}
    {sources.length > 0 && (
      <p className="mt-1 text-[11px] text-muted-foreground" data-testid="funeral-cost-sources">
        Sources: {sources.map((s, i) => <span key={s.url}>{i > 0 && " · "}<a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="text-primary hover:underline" title={s.title} data-testid="funeral-cost-source-link">{s.publisher || s.title} ↗</a></span>)}
      </p>
    )}
  </div>
);

export const RightsList = ({ rights }) => (
  <ul className="grid sm:grid-cols-2 gap-2" data-testid="funeral-rights">
    {rights.map((r, i) => <li key={r.t} data-testid={`funeral-right-${i}`} className="rounded-xl border border-border p-3 text-sm flex gap-2"><ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" /><div><div className="font-medium">{r.t}</div><div className="text-xs text-muted-foreground mt-0.5">{r.d}</div></div></li>)}
  </ul>
);

export const HelpList = ({ help, linkedDocs }) => (
  <div data-testid="funeral-help" className="space-y-2">
    {help.map((h) => (
      <div key={h.id} className="rounded-xl border border-border p-3 text-sm flex gap-2" data-testid={`funeral-help-${h.id}`}>
        <HandCoins className="h-4 w-4 text-primary shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="font-medium">{h.t}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{h.d}</div>
          {h.url && <a href={h.url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1">{h.cta} <ExternalLink className="h-3 w-3" /></a>}
          {h.id === "insurance" && linkedDocs && (
            linkedDocs.length ? <ul className="mt-2 space-y-1" data-testid="funeral-linked-docs">{linkedDocs.map((d) => <li key={d.id} className="text-xs flex items-center gap-1.5">{d.sensitive ? <Lock className="h-3 w-3 text-primary" /> : <FileText className="h-3 w-3 text-primary" />}<Link to={`/vault?doc=${d.id}`} className="hover:text-primary truncate">{d.title}</Link><span className="ml-auto font-mono text-muted-foreground">{d.category}</span></li>)}</ul>
              : <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-400" data-testid="funeral-linked-docs-empty">{tr("No pre-need contract, life insurance or burial-plot papers found in the vault yet — upload them and they'll appear here.")}</p>
          )}
        </div>
      </div>
    ))}
  </div>
);

export const LinksList = ({ links }) => (
  <ul className="space-y-2" data-testid="funeral-links">
    {links.map((l) => <li key={l.id} className="rounded-xl border border-border p-3 text-sm flex gap-2" data-testid={`funeral-link-${l.id}`}><MapPinned className="h-4 w-4 text-primary shrink-0 mt-0.5" /><div><div className="font-medium">{l.t}</div><div className="text-xs text-muted-foreground mt-0.5">{l.d}</div><a href={l.url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-1">{l.cta} <ExternalLink className="h-3 w-3" /></a></div></li>)}
  </ul>
);

export const QuotesTable = ({ quotes, quoteItems }) => {
  if (!quotes?.length) return null;
  return (
    <div className="rounded-xl border border-border overflow-x-auto" data-testid="funeral-quotes-table">
      <table className="w-full text-xs">
        <thead className="bg-muted/60"><tr><th className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{tr("Item")}</th>{quotes.map((q) => <th key={q.id} className="text-right px-3 py-2 font-medium">{q.name || "Quote"}</th>)}</tr></thead>
        <tbody className="divide-y divide-border">
          {quoteItems.map((it) => <tr key={it.key}><td className="px-3 py-1.5 text-muted-foreground">{it.label}</td>{quotes.map((q) => <td key={q.id} className="px-3 py-1.5 text-right font-mono">{q.items?.[it.key] ? money(q.items[it.key]) : "—"}</td>)}</tr>)}
          <tr className="bg-muted/40 font-medium"><td className="px-3 py-2">{tr("Total")}</td>{quotes.map((q) => <td key={q.id} className="px-3 py-2 text-right font-mono" data-testid={`funeral-quote-total-${q.id}`}>{money(q.total)}</td>)}</tr>
        </tbody>
      </table>
    </div>
  );
};
